import axios from 'axios';
import { getAccessToken } from './auth';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// Create axios instance with auth interceptor
const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use(async (config) => {
  try {
    const token = await getAccessToken();
    config.headers.Authorization = `Bearer ${token}`;
  } catch (err) {
    console.error('Failed to acquire token:', err);
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Do NOT reload on 401 — let components handle gracefully
    // A reload loop would occur if token acquisition keeps failing
    return Promise.reject(error);
  }
);

// ── Dashboard ──
export const getDashboardKPIs = () => api.get('/dashboard/kpis');
export const getDashboardAlerts = () => api.get('/dashboard/alerts');
export const getRecentRequests = () => api.get('/dashboard/recent-requests');
export const getExpiringAgreements = () => api.get('/dashboard/expiring-agreements');
export const getCasesByStage = () => api.get('/dashboard/cases-by-stage');
export const getComplianceScore = () => api.get('/dashboard/compliance-score');

// ── Requests ──
export const getRequests = (params) => api.get('/requests', { params });
export const getRequest = (id) => api.get(`/requests/${id}`);
export const createRequest = (data) => api.post('/requests', data);
export const updateRequest = (id, data) => api.patch(`/requests/${id}`, data);

// ── Agreements ──
export const getAgreements = (params) => api.get('/agreements', { params });
export const getAgreement = (id) => api.get(`/agreements/${id}`);
export const createAgreement = (data) => api.post('/agreements', data);
export const updateAgreement = (id, data) => api.patch(`/agreements/${id}`, data);
export const importAgreements = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/agreements/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// ── Cases ──
export const getCases = (params) => api.get('/cases', { params });
export const getCase = (id) => api.get(`/cases/${id}`);
export const createCase = (data) => api.post('/cases', data);
export const updateCase = (id, data) => api.patch(`/cases/${id}`, data);
export const addCaseEvent = (id, data) => api.post(`/cases/${id}/events`, data);
export const addCaseCost = (id, data) => api.post(`/cases/${id}/costs`, data);

// ── ITT ──
export const getITTRecords = (params) => api.get('/itt', { params });
export const createITTRecord = (data) => api.post('/itt', data);
export const updateITTRecord = (id, data) => api.patch(`/itt/${id}`, data);
export const getITTChecklist = (id) => api.get(`/itt/${id}/checklist`);
export const addChecklistItem = (id, data) => api.post(`/itt/${id}/checklist`, data);
export const exportITTRegister = () => api.get("/itt/export/csv", { responseType: 'blob' });

// ── Signatures ──
export const getSignatures    = () => api.get('/signatures');
export const applySignature   = (data) => api.post('/signatures/apply', data);
export const getSignatureLog  = () => api.get('/signatures/log');
// Upload PDF → backend stamps it → returns signed PDF as blob
export const stampSignedDoc   = (formData) => api.post('/signatures/stamp', formData, {
  headers: { 'Content-Type': 'multipart/form-data' },
  responseType: 'arraybuffer',
});

// ── Compliance ──
export const importObligations = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/compliance/obligations/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const getObligations = (params) => api.get('/compliance/obligations', { params });
export const createObligation = (data) => api.post('/compliance/obligations', data);
export const updateObligation = (id, data) => api.patch(`/compliance/obligations/${id}`, data);
export const getObligationHistory = (id) => api.get(`/compliance/obligations/${id}/history`);
export const backfillCycles = (dryRun = true) => api.post('/compliance/obligations/backfill-cycles', { dry_run: dryRun });
export const getIncidents = () => api.get('/compliance/incidents');
export const createIncident = (data) => api.post('/compliance/incidents', data);
export const getPolicies = () => api.get('/compliance/policies');
export const getComplianceScores = () => api.get('/compliance/scores');

// ── Meetings ──
export const getMeetings = () => api.get('/meetings');
export const createMeeting = (data) => api.post('/meetings', data);
export const updateMeeting = (id, data) => api.patch(`/meetings/${id}`, data);
export const getResolutions = (meetingId) => api.get(`/meetings/${meetingId}/resolutions`);
export const createResolution = (meetingId, data) => api.post(`/meetings/${meetingId}/resolutions`, data);

// ── Notifications ──
export const getNotifications = () => api.get('/notifications');
export const markRead = (id) => api.patch(`/notifications/${id}/read`);
export const markAllRead = () => api.patch('/notifications/read-all');

// ── Documents ──
export const uploadDocument = (formData) => api.post('/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getDocuments = (params) => api.get('/documents', { params });

// ── Users ──
export const getUsers = () => api.get('/users');
export const getCurrentUser = () => api.get('/users/me');
export const getLegalTeam = () => api.get('/users/legal-team');
export const updateUserRole = (id, role) => api.patch(`/users/${id}/role`, { role });

// ── Search ──
export const globalSearch = (q) => api.get('/search', { params: { q } });

// ── Audit ──
export const getAuditLog = (params) => api.get('/audit', { params });

// ── Drafts (FR-011 to FR-015) ──
export const getDrafts = (params) => api.get('/drafts', { params });
export const getDraft = (id) => api.get(`/drafts/${id}`);
export const createDraft = (data) => api.post('/drafts', data);
export const updateDraft = (id, data) => api.patch(`/drafts/${id}`, data);
export const getDraftTurnaround = () => api.get('/drafts/stats/turnaround');

// ── Review Comments & Workflow (FR-017) ──
export const getReviewComments = (requestId) => api.get(`/reviews/${requestId}/comments`);
export const addReviewComment = (requestId, data) => api.post(`/reviews/${requestId}/comments`, data);
export const acceptReview = (requestId) => api.patch(`/reviews/${requestId}/accept`);
export const getRequestDocuments = (requestId) => api.get('/documents', { params: { linked_entity_type: 'request', linked_entity_id: requestId } });

// ── Signature Admin (FR-026 to FR-032) ──
export const uploadSignature = (formData) => api.post('/signatures/admin/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const applySignatureWithHash = (formData) => api.post('/signatures/admin/apply-with-hash', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

// ── Reports (FR-049 to FR-052, FR-073) ──
export const getLegalActivityReport = () => api.get('/reports/legal-activity');
export const getAgreementExpiryReport = () => api.get('/reports/agreement-expiry');
export const getITTPipelineReport = () => api.get('/reports/itt-pipeline');
export const getComplianceStatusReport = () => api.get('/reports/compliance-status');
export const getTurnaroundReport = () => api.get('/reports/turnaround');

// ── Staff Directory (FR-077, FR-078) ──
export const getStaffDirectory = () => api.get('/staff/directory');
export const getManager = (userId) => api.get(`/staff/manager/${userId}`);

export default api;
